// components/b2b/Dashboard.jsx
import React from 'react';
import { Card } from '../ui/Card';
import { Icon } from '../ui/Icon';
import { useBrand } from '../../contexts/BrandContext';

export function B2BDashboard({ user, onNavigate }) {
  const { brand } = useBrand();
  
  const stats = [
    {
      label: 'YTD Direct Spend',
      value: '$247K',
      change: '+18%',
      icon: 'dollar',
      color: brand.c.accent
    },
    {
      label: 'Savings vs GPO',
      value: '$73K',
      change: '+22%',
      icon: 'trendUp',
      color: '#10B981'
    },
    {
      label: 'Connected Vendors',
      value: '2',
      change: 'Active',
      icon: 'link',
      color: '#F59E0B'
    },
    {
      label: 'Active POs',
      value: '47',
      change: 'This month',
      icon: 'clipboard',
      color: '#7C3AED'
    }
  ];
  
  const quickActions = [
    {
      icon: 'grid',
      title: 'Browse Catalog',
      description: 'View products with direct pricing',
      action: 'catalog',
      color: brand.c.accent
    },
    {
      icon: 'link',
      title: 'Vendor Connections',
      description: 'Manage your vendor integrations',
      action: 'vendors',
      color: '#10B981'
    },
    {
      icon: 'barChart',
      title: 'Spend Analysis',
      description: 'View cost savings reports',
      action: 'savings',
      color: '#7C3AED'
    }
  ];
  
  const recentActivity = [
    { type: 'po', id: 'PO-2024-089', description: 'Purchase order placed - 15 items', timestamp: '2 hours ago', icon: 'clipboard', color: brand.c.accent },
    { type: 'sync', id: 'McKesson', description: 'Vendor sync completed - 40 products updated', timestamp: '5 hours ago', icon: 'sync', color: '#10B981' },
    { type: 'po', id: 'PO-2024-088', description: 'Purchase order delivered', timestamp: '1 day ago', icon: 'checkCircle', color: '#10B981' },
    { type: 'vendor', id: 'Cardinal Health', description: 'New vendor connection established', timestamp: '2 days ago', icon: 'link', color: '#F59E0B' }
  ];
  
  return (
    <div style={{
      background: '#F9FAFB',
      minHeight: '100vh',
      padding: '40px 24px',
      fontFamily: 'Libre Franklin, sans-serif'
    }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        {/* Welcome */}
        <div style={{ marginBottom: 40 }}>
          <h1 style={{ fontSize: 32, fontWeight: 700, marginBottom: 8, color: brand.c.navy }}>
            Welcome back, {user.firstName}!
          </h1>
          <p style={{ fontSize: 16, color: '#6B7280' }}>
            Here's your procurement overview for {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </p>
        </div>
        
        {/* Stats Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: 20,
          marginBottom: 48
        }}>
          {stats.map((stat, i) => (
            <Card key={i}>
              <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between' }}>
                <div>
                  <div style={{
                    fontSize: 14,
                    color: '#6B7280',
                    marginBottom: 8,
                    fontWeight: 600
                  }}>
                    {stat.label}
                  </div>
                  <div style={{
                    fontSize: 36,
                    fontWeight: 900,
                    color: stat.color,
                    fontFamily: "'Merriweather', serif",
                    marginBottom: 4
                  }}>
                    {stat.value}
                  </div>
                  <div style={{
                    fontSize: 12,
                    color: stat.change.includes('+') ? '#10B981' : '#6B7280',
                    fontWeight: 600
                  }}>
                    {stat.change}
                  </div>
                </div>
                <div style={{
                  width: 48,
                  height: 48,
                  borderRadius: 10,
                  background: `${stat.color}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Icon name={stat.icon} size={24} color={stat.color} />
                </div>
              </div>
            </Card>
          ))}
        </div>
        
        {/* Quick Actions */}
        <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 20, color: brand.c.navy }}>
          Quick Actions
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 20,
          marginBottom: 48
        }}>
          {quickActions.map((action, i) => (
            <Card key={i} hover onClick={() => onNavigate(action.action)}>
              <div style={{
                width: 56,
                height: 56,
                borderRadius: 12,
                background: `${action.color}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 16
              }}>
                <Icon name={action.icon} size={28} color={action.color} />
              </div>
              <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8, color: brand.c.navy }}>
                {action.title}
              </h3>
              <p style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.5 }}>
                {action.description}
              </p>
            </Card>
          ))}
        </div>
        
        {/* Recent Activity */}
        <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 20, color: brand.c.navy }}>
          Recent Activity
        </h2>
        <Card>
          <div style={{ display: 'grid', gap: 16 }}>
            {recentActivity.map((activity, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  gap: 16,
                  alignItems: 'start',
                  padding: 16,
                  background: '#F9FAFB',
                  borderRadius: 8
                }}
              >
                <div style={{
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  background: `${activity.color}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <Icon name={activity.icon} size={20} color={activity.color} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, marginBottom: 4, color: brand.c.navy }}>
                    {activity.id}
                  </div>
                  <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 4 }}>
                    {activity.description}
                  </div>
                  <div style={{ fontSize: 12, color: '#9CA3AF' }}>
                    {activity.timestamp}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}